import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, User, Receipt, KeyRound, Eye, EyeOff, Loader2, Search } from "lucide-react";
import { can, getMe } from "@/lib/permissions";
import { ClientServicesPicker } from "@/components/ClientServicesPicker";
import ClientInvoiceDialog from "@/components/ClientInvoiceDialog";
import { lookupCep, maskCep } from "@/lib/viacep";

// The address is split into structured fields so we can forward them to
// Asaas (postalCode, address, addressNumber, complement, province, city,
// state) — required for NF-e (NFS-e) emission through the Asaas dashboard.
const empty = {
  name: "", email: "", phone: "", cpf_cnpj: "",
  cep: "", address_street: "", address_number: "", address_complement: "",
  neighborhood: "", city: "", state: "",
};

export default function Clients() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [invoiceClient, setInvoiceClient] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPass, setResetPass] = useState("");
  const [resetShow, setResetShow] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const me = getMe();
  const isAdmin = me?.role === "owner" || me?.role === "platform_admin" || me?.is_platform_admin;

  const load = async () => setList((await api.get("/clients")).data);
  useEffect(() => {
    load();
    if (isAdmin) api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, [isAdmin]);

  const openNew = () => { setEditing(null); setForm(empty); setAllowed([]); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setAllowed(c.allowed_services || []);
    setForm({
      name: c.name || "", email: c.email || "", phone: c.phone || "", cpf_cnpj: c.cpf_cnpj || "",
      cep: c.cep || "", address_street: c.address_street || "", address_number: c.address_number || "",
      address_complement: c.address_complement || "", neighborhood: c.neighborhood || "",
      city: c.city || "", state: c.state || "",
    });
    setOpen(true);
  };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Autofill street/neighborhood/city/state via ViaCEP so the user only has
  // to type CEP + number. The lookup is triggered on blur/Enter.
  const doCepLookup = async () => {
    const digits = (form.cep || "").replace(/\D/g, "");
    if (digits.length !== 8) { toast.error("Informe um CEP com 8 dígitos"); return; }
    setCepLoading(true);
    try {
      const res = await lookupCep(digits);
      if (!res) { toast.error("CEP não encontrado"); return; }
      setForm((f) => ({
        ...f,
        cep: maskCep(digits),
        address_street: res.street || f.address_street,
        neighborhood: res.neighborhood || f.neighborhood,
        city: res.city || f.city,
        state: res.state || f.state,
      }));
      toast.success("Endereço preenchido");
    } finally { setCepLoading(false); }
  };

  const save = async () => {
    const payload = { ...form, email: form.email || null };
    try {
      const { data } = editing ? await api.put(`/clients/${editing.id}`, payload) : await api.post("/clients", payload);
      if (isAdmin) await api.put(`/clients/${data.id}/allowed-services`, { service_ids: allowed.filter((id) => services.some((s) => s.id === id)) });
      toast.success("Cliente salvo"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
  };
  const remove = async (id) => { try { await api.delete(`/clients/${id}`); toast.success("Removido"); load(); } catch { toast.error("Falha ao remover"); } };

  const submitReset = async () => {
    if (!resetTarget || (resetPass || "").length < 6) { toast.error("Senha deve ter ao menos 6 caracteres"); return; }
    setResetSaving(true);
    try {
      await api.post(`/clients/${resetTarget.id}/reset-password`, { password: resetPass });
      toast.success(`Nova senha definida para ${resetTarget.name}. Ele será solicitado a trocar no próximo login.`);
      setResetTarget(null); setResetPass("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao redefinir"); }
    finally { setResetSaving(false); }
  };

  const filtered = list.filter((c) => {
    const t = q.toLowerCase();
    return !t || c.name?.toLowerCase().includes(t) || c.phone?.toLowerCase().includes(t) || c.email?.toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Clientes</h1>
          <p className="text-sm text-muted-foreground mt-1">Base de clientes do estabelecimento.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-client" onClick={openNew} className="h-11" disabled={!can("clients.write")}><Plus className="h-4 w-4 mr-2" /> Novo cliente</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
              <DialogDescription>
                {editing ? "Atualize dados de contato e endereço do cliente." : "Cadastre um novo cliente com endereço completo para emissão de NF-e no Asaas."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input data-testid="input-client-name" value={form.name} onChange={(e) => set("name")(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Telefone</Label><Input data-testid="input-client-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} /></div>
                <div className="space-y-2"><Label>Email</Label><Input data-testid="input-client-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} /></div>
              </div>
              <div className="space-y-2"><Label>CPF/CNPJ (obrig. p/ cobrança Asaas)</Label><Input data-testid="input-client-cpf" value={form.cpf_cnpj} onChange={(e) => set("cpf_cnpj")(e.target.value)} /></div>

              {/* Endereço estruturado — enviado ao Asaas para emissão de NF-e. */}
              <div className="pt-2 border-t border-border">
                <div className="text-sm font-medium mb-2">Endereço (para NF-e)</div>
                <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <Input
                      data-testid="input-client-cep"
                      value={form.cep}
                      onChange={(e) => set("cep")(maskCep(e.target.value))}
                      onBlur={() => { if ((form.cep || "").replace(/\D/g, "").length === 8) doCepLookup(); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doCepLookup(); } }}
                      placeholder="00000-000"
                      inputMode="numeric"
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={doCepLookup} disabled={cepLoading} data-testid="btn-client-cep-lookup" className="h-10">
                    {cepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Buscar</>}
                  </Button>
                </div>
                <div className="grid grid-cols-[1fr_120px] gap-3 mt-3">
                  <div className="space-y-2"><Label>Rua/Logradouro</Label><Input data-testid="input-client-street" value={form.address_street} onChange={(e) => set("address_street")(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Número</Label><Input data-testid="input-client-num" value={form.address_number} onChange={(e) => set("address_number")(e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-2"><Label>Complemento</Label><Input data-testid="input-client-comp" value={form.address_complement} onChange={(e) => set("address_complement")(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Bairro</Label><Input data-testid="input-client-neigh" value={form.neighborhood} onChange={(e) => set("neighborhood")(e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-[1fr_100px] gap-3 mt-3">
                  <div className="space-y-2"><Label>Cidade</Label><Input data-testid="input-client-city" value={form.city} onChange={(e) => set("city")(e.target.value)} /></div>
                  <div className="space-y-2"><Label>UF</Label><Input data-testid="input-client-state" maxLength={2} value={form.state} onChange={(e) => set("state")(e.target.value.toUpperCase())} /></div>
                </div>
              </div>
              {isAdmin && <ClientServicesPicker services={services} value={allowed} onChange={setAllowed} />}
            </div>
            <DialogFooter><Button data-testid="btn-save-client" onClick={save}>Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Input data-testid="input-search-clients" placeholder="Buscar por nome, telefone ou email" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 max-w-md" />

      <div className="surface divide-y divide-border">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`client-row-${c.id}`} className="flex items-center gap-4 p-4 hover:bg-muted/40 transition-colors">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><User className="h-5 w-5 text-muted-foreground" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{c.name}</div>
              <div className="text-xs text-muted-foreground truncate">{[c.phone, c.email, c.cpf_cnpj].filter(Boolean).join(" · ")}</div>
            </div>
            {can("payments.charge") && (
              <Button data-testid={`btn-invoice-client-${c.id}`} variant="outline" size="sm" onClick={() => setInvoiceClient(c)}><Receipt className="h-4 w-4 mr-1" /> Cobrar</Button>
            )}
            {can("clients.write") && (
              <Button data-testid={`btn-reset-pw-${c.id}`} variant="ghost" size="sm" onClick={() => { setResetTarget(c); setResetPass(""); }} title="Redefinir senha do cliente"><KeyRound className="h-4 w-4" /></Button>
            )}
            <Button data-testid={`btn-edit-client-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)} disabled={!can("clients.write")}><Pencil className="h-4 w-4" /></Button>
            {can("clients.delete") && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button data-testid={`btn-delete-client-${c.id}`} variant="ghost" size="sm" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Excluir "{c.name}"?</AlertDialogTitle><AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(c.id)}>Excluir</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="p-12 text-center text-muted-foreground">Nenhum cliente encontrado.</div>}
      </div>

      <ClientInvoiceDialog client={invoiceClient} open={!!invoiceClient} onClose={() => setInvoiceClient(null)} />

      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetPass(""); setResetShow(false); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-reset-pw">
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>
              Defina uma senha temporária para {resetTarget?.name || "o cliente"}. Ele será obrigado a criar uma senha pessoal no próximo login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Nova senha temporária (mín. 6)</Label>
              <div className="relative">
                <Input data-testid="input-reset-pw" type={resetShow ? "text" : "password"} value={resetPass} onChange={(e) => setResetPass(e.target.value)} placeholder="Ex.: senha1234" autoFocus className="pr-10" />
                <button type="button" onClick={() => setResetShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={resetShow ? "Esconder" : "Mostrar"} data-testid="btn-toggle-reset-pw">
                  {resetShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-xs text-muted-foreground">Combine com o cliente pelo WhatsApp/telefone e informe essa senha para ele entrar.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setResetPass(""); setResetShow(false); }}>Cancelar</Button>
            <Button onClick={submitReset} disabled={resetSaving || (resetPass || "").length < 6} data-testid="btn-confirm-reset-pw">Definir senha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
